import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  User,
  Mail,
  Shield,
  Key,
  Eye,
  EyeOff,
  Save,
  UserCog,
  Trash2,
  AlertTriangle,
  ShieldCheck,
} from "lucide-react";

const SettingsPage = () => {
  const { user, changePassword, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [loading, setLoading] = useState(false);
  const [changingRole, setChangingRole] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("A nova senha deve ter pelo menos 6 caracteres");
      return;
    }

    setLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success("Senha alterada com sucesso!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao alterar senha");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeRole = async () => {
    setChangingRole(true);
    try {
      await api.put("/auth/change-role");
      toast.success("Papel alterado para Administrador com sucesso!");
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
      // Reload page to update permissions
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao alterar papel");
    } finally {
      setChangingRole(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      await api.delete("/auth/delete-account");
      toast.success("Conta excluída com sucesso");
      logout();
      navigate("/login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir conta");
    } finally {
      setDeletingAccount(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
          Configurações
        </h1>
        <p className="text-slate-500 mt-1">
          Gerencie suas configurações e perfil
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Info */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              Informações do Perfil
            </CardTitle>
            <CardDescription>
              Seus dados cadastrados no sistema
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center">
                <span className="text-2xl font-bold text-white">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-lg font-semibold text-slate-900">{user?.name}</p>
                <p className="text-sm text-slate-500 capitalize">
                  {user?.role === "admin" ? "Administrador" : "Colaborador"}
                </p>
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">E-mail</p>
                  <p className="font-medium">{user?.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Função</p>
                  <p className="font-medium capitalize">
                    {user?.role === "admin" ? "Administrador" : "Colaborador"}
                  </p>
                </div>
              </div>

              {user?.role === "collaborator" && user?.permissions && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">Permissões</p>
                  <div className="flex flex-wrap gap-2">
                    {user.permissions.map((perm) => (
                      <span
                        key={perm}
                        className="px-2 py-1 bg-slate-100 rounded text-xs font-medium text-slate-600"
                      >
                        {perm === "view" && "Visualizar"}
                        {perm === "add_entry" && "Adicionar Entrada"}
                        {perm === "add_exit" && "Adicionar Saída"}
                        {perm === "edit" && "Editar"}
                        {perm === "delete" && "Excluir"}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Key className="h-5 w-5 text-primary" />
              Alterar Senha
            </CardTitle>
            <CardDescription>
              Atualize sua senha de acesso
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Senha Atual</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showPasswords ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    data-testid="current-password-input"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">Nova Senha</Label>
                <Input
                  id="newPassword"
                  type={showPasswords ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
                <Input
                  id="confirmPassword"
                  type={showPasswords ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="change-password-btn"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvar Nova Senha
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Account Management */}
      <Card className="card-dashboard border-amber-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <UserCog className="h-5 w-5 text-amber-600" />
            Gerenciamento da Conta
          </CardTitle>
          <CardDescription>
            Opções avançadas para sua conta
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Change Role - Only for non-admins */}
          {user?.role !== "admin" && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-blue-900">Tornar-se Administrador</h4>
                  <p className="text-sm text-blue-700 mt-1 mb-4">
                    Se você criou sua conta antes da atualização do sistema e deveria ser administrador, 
                    clique no botão abaixo para obter acesso completo.
                  </p>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100">
                        <Shield className="mr-2 h-4 w-4" />
                        Tornar-me Administrador
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                          <Shield className="h-5 w-5 text-blue-600" />
                          Confirmar Alteração de Papel
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          Você está prestes a se tornar <strong>Administrador</strong>. 
                          Isso lhe dará acesso completo a todas as funcionalidades do sistema, incluindo:
                          <ul className="list-disc list-inside mt-2 space-y-1">
                            <li>Gerenciar usuários (Equipe)</li>
                            <li>Acessar configurações do sistema</li>
                            <li>Visualizar log de auditoria</li>
                            <li>Gerenciar backups</li>
                            <li>Acessar a lixeira</li>
                          </ul>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleChangeRole}
                          disabled={changingRole}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          {changingRole ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          ) : (
                            "Confirmar"
                          )}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          )}

          {/* Delete Account */}
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-red-900">Excluir Conta</h4>
                <p className="text-sm text-red-700 mt-1 mb-4">
                  Esta ação é <strong>irreversível</strong>. Todos os seus dados serão permanentemente removidos do sistema.
                  {user?.role === "admin" && (
                    <span className="block mt-1 font-medium">
                      ⚠️ Administradores só podem excluir a conta se houver outro administrador no sistema.
                    </span>
                  )}
                </p>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-100">
                      <Trash2 className="mr-2 h-4 w-4" />
                      Excluir Minha Conta
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                        <AlertTriangle className="h-5 w-5" />
                        Tem certeza que deseja excluir sua conta?
                      </AlertDialogTitle>
                      <AlertDialogDescription className="space-y-2">
                        <p>
                          Esta ação é <strong>permanente e irreversível</strong>. 
                          Ao excluir sua conta:
                        </p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>Você será desconectado imediatamente</li>
                          <li>Não poderá mais acessar o sistema</li>
                          <li>Seus dados de perfil serão removidos</li>
                        </ul>
                        <p className="font-medium mt-3">
                          ⚠️ Para continuar usando o sistema, você precisará que um administrador crie uma nova conta para você.
                        </p>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteAccount}
                        disabled={deletingAccount}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        {deletingAccount ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        ) : (
                          "Sim, Excluir Minha Conta"
                        )}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Info */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="font-heading text-lg">
            Sobre o Sistema
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-slate-500">Sistema</p>
              <p className="font-medium">EstoqueClinic</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Versão</p>
              <p className="font-medium">1.0.0</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Desenvolvido para</p>
              <p className="font-medium">Clínicas de Estética</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
